<template>
  <div class="RMPE-CustomForm StaticForm">
    <form
      novalidate
      @submit.prevent="handlFormSubmission"
    >
      <div
        v-for="question in questions"
        :key="question.id"
      >
        <component
          :is="resolveField(question)"
          v-show="calculateFieldVisibility(question)"
          v-model="form[question.id]"
          :question="question"
          :error="validateStatus({ field: $v.form[question.id], serverKey: question.id, label: question.label })"
          :has-autofocus="questionWithAutofocus === question.id"
          :manual-validate="formState.validateAll"
          @change="validateBlur({ field: $v.form[question.id], serverKey: question.id })"
          @show-popup="formState.showPopup = true"
        />
      </div>
      <div
        v-if="formState.showPopup && config.optInStatement !== null"
        class="RMPE-CustomOptIn"
      >
        <font-awesome
          class="RMPE-CustomOptIn-CloseIcon"
          :icon="closeIcon"
          @click="formState.showPopup = false"
        />
        <pre
          class="RMPE-CustomOptIn-Content"
          v-html="config.optInStatement"
        />
      </div>
      <slot name="submit">
        <button
          type="submit"
          class="button is-primary RMPE-CustomSubmit"
          :class="{ 'is-loading': isSubmitting }"
        >
          {{ config.registerButton }}
        </button>
      </slot>
    </form>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import {
  required, email, decimal, url,
} from 'vuelidate/lib/validators';
import {
  fullName,
  checkboxRequired,
  maxUploadedFileSizeValidator,
  mimeTypeUploadedFileValidator,
} from '../validators';
import CheckboxField from '../fields/static/checkbox-field.vue';
import CheckboxGroupField from '../fields/static/checkbox-group-field.vue';
import EmailField from '../fields/static/email-field.vue';
import LabelField from '../fields/static/label-field.vue';
import RadioField from '../fields/static/radio-field.vue';
import SelectField from '../fields/static/select-field.vue';
import TextField from '../fields/static/text-field.vue';
import NumberField from '../fields/static/number-field.vue';
import TelephoneField from '../fields/static/telephone-field.vue';
import TextareaField from '../fields/static/textarea-field.vue';
import AutocompleteField from '../fields/static/autocomplete-field.vue';
import DateField from '../fields/static/date-field.vue';
import TimeField from '../fields/static/time-field.vue';
import RatingField from '../fields/static/rating-field.vue';
import FileUploadField from '../fields/static/file-upload-field.vue';
import ValidationMixin from '../mixins/validation-mixin';
import Lang from '../lang/index';

export default {
  components: {
    FontAwesome: FontAwesomeIcon,
  },

  mixins: [ValidationMixin],

  props: {
    questions: {
      type: Array,
      default: () => [],
    },
    config: {
      type: Object,
      default: () => {},
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isSubmitting: {
      type: Boolean,
      default: false,
    },
    autoFocusFirstField: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    const formStructure = this.questions
      .filter((question) => question.type !== 'label')
      .reduce((carry, question) => {
        carry[question.id] = question.type === 'checkbox' ? false : null;
        return carry;
      }, {});

    return {
      form: formStructure,
      closeIcon: faTimes,
      formState: {
        validateAll: false,
        loading: false,
        showPopup: false,
      },
      serverValidationErrors: {},
    };
  },

  computed: {
    lang() {
      return Lang;
    },
    questionWithAutofocus() {
      if (!this.autoFocusFirstField) return null;

      const autofocusQuestion = this.questions.find((question) => {
        return ((question.type !== 'label' && question.type !== 'radio') && this.calculateFieldVisibility(question));
      });

      if (autofocusQuestion === undefined || autofocusQuestion.type === 'autocomplete') return null;

      return autofocusQuestion.id;
    },
  },

  watch: {
    form: {
      deep: true,
      immediate: true,
      handler() {
        this.$emit('custom-form:submit-payload-changed', this.formatPayload(this.form));
      },
    },
  },

  methods: {
    resolveField(question) {
      /* eslint-disable no-else-return */
      if (question.type === 'checkbox') {
        return CheckboxField;
      } else if (question.type === 'checkboxGroup') {
        return CheckboxGroupField;
      } else if (question.type === 'email') {
        return EmailField;
      } else if (question.type === 'select') {
        return SelectField;
      } else if (question.type === 'radio') {
        return RadioField;
      } else if (question.type === 'label') {
        return LabelField;
      } else if (question.type === 'text' && question.subtype === 'multiline') {
        return TextareaField;
      } else if (question.type === 'number') {
        return NumberField;
      } else if (question.type === 'tel') {
        return TelephoneField;
      } else if (question.type === 'autocomplete') {
        return AutocompleteField;
      } else if (question.type === 'date' && question.subtype === 'date') {
        return DateField;
      } else if (question.type === 'date' && question.subtype === 'time') {
        return TimeField;
      } else if (question.type === 'rating') {
        return RatingField;
      } else if (question.type === 'text') {
        return TextField;
      } else if (question.type === 'file_upload') {
        return FileUploadField;
      }
      /* eslint-enable no-else-return */

      return TextField;
    },

    handlFormSubmission() {
      if (this.loading) {
        return false;
      }

      this.formState.validateAll = true;

      if (this.$v.form.$invalid) {
        this.$nextTick(() => {
          this.scrollToFirstFieldWithError();
        });

        this.$emit('custom-form:validation-errors');
        return false;
      }

      this.serverValidationErrors = {};

      const payload = this.formatPayload(this.form);

      return this.$emit('custom-form:submit', { payload });
    },

    formatPayload(form) {
      return Object.keys(form)
        .filter((field) => {
          const fieldToCheck = this.questions.find((question) => question.id === field);
          return this.isQuestionVisibleViaConditionalLogic(fieldToCheck) && this.form[field] !== null;
        }).reduce((acc, field) => {
          acc[field] = this.form[field];
          return acc;
        }, {});
    },

    calculateFieldVisibility(question) {
      if (question.hidden === true) return false;

      return this.isQuestionVisibleViaConditionalLogic(question);
    },

    isQuestionVisibleViaConditionalLogic(question) {
      if (!question.displayed) return true;

      const questionIdToCheckAgainst = Object.keys(question.displayed)[0];

      const questionObjectToCheckAgain = this.questions.find((field) => field.id === questionIdToCheckAgainst);

      const lowercaseString = (value) => {
        return typeof value === 'string' ? value.toLowerCase() : `${value}`.toLowerCase();
      };

      const testArrayIncludes = (valueToTestAgainst) => {
        if (!Array.isArray(this.form[questionIdToCheckAgainst])) return false;

        return this.form[questionIdToCheckAgainst].map((value) => {
          return lowercaseString(value);
        }).includes(lowercaseString(valueToTestAgainst));
      };

      const testStringContains = (valueToTestAgainst) => {
        const answerToCheck = lowercaseString(this.form[questionIdToCheckAgainst]);
        const valueToCompareAgainst = lowercaseString(valueToTestAgainst);

        return (typeof this.form[questionIdToCheckAgainst] === 'string')
          && answerToCheck.includes(valueToCompareAgainst);
      };

      const testStringNotEmpty = () => {
        return this.form[questionIdToCheckAgainst] !== null
          && this.form[questionIdToCheckAgainst].length !== 0;
      };

      // Field only visible if other field visible
      if (question.displayed[questionIdToCheckAgainst].$displayed === true) {
        return this.calculateFieldVisibility(questionObjectToCheckAgain);
      }

      // Field only visible if other field is Not Equals Null
      if (question.displayed[questionIdToCheckAgainst].$ne === null) {
        return testStringNotEmpty();
      }

      // Field only visible if other field is Not Equals a 'String'
      if (question.displayed[questionIdToCheckAgainst].$ne) {
        return lowercaseString(this.form[questionIdToCheckAgainst]) !== lowercaseString(question.displayed[questionIdToCheckAgainst].$ne);
      }

      // Field only visible if other field equals 'string'
      if (typeof question.displayed[questionIdToCheckAgainst] === 'string') {
        console.log(lowercaseString(this.form[questionIdToCheckAgainst]), lowercaseString(question.displayed[questionIdToCheckAgainst]));
        return lowercaseString(this.form[questionIdToCheckAgainst]) === lowercaseString(question.displayed[questionIdToCheckAgainst]);
      }

      if (question.displayed[questionIdToCheckAgainst].$nin) {
        const valueToTestAgainst = question.displayed[questionIdToCheckAgainst].$nin[0];
        return !testArrayIncludes(valueToTestAgainst) && !testStringContains(valueToTestAgainst);
      }

      if (question.displayed[questionIdToCheckAgainst].$in) {
        const valueToTestAgainst = question.displayed[questionIdToCheckAgainst].$in[0];
        return testArrayIncludes(valueToTestAgainst) || testStringContains(valueToTestAgainst);
      }

      if (typeof question.displayed[questionIdToCheckAgainst] === 'boolean') {
        return this.form[questionIdToCheckAgainst] === question.displayed[questionIdToCheckAgainst];
      }

      return false;
    },

    scrollToFirstFieldWithError() {
      const element = document.querySelector('.RMPE-HasError');

      if (element === null) return;

      const topOffset = 30;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition - topOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    },
  },

  validations() {
    const formStructure = this.questions
      .filter(question => question.type !== 'label')
      .map((question) => {
        // Fields are only required if visible and set as required. this stops conditional fields breaking validation logic.
        let validationRules = question.required === true && this.calculateFieldVisibility(question) ? { required } : {};

        if (question.type === 'email') {
          validationRules = { ...validationRules, email };
        } else if (question.id === 'fullName') {
          validationRules = { ...validationRules, fullName };
        } else if (question.type === 'number') {
          validationRules = { ...validationRules, numeric: decimal };
        } else if (question.type === 'checkbox' && question.required === true) {
          validationRules = { ...validationRules, checkboxRequired };
        } else if (question.type === 'url') {
          validationRules = { ...validationRules, url };
        } else if (question.type === 'file_upload') {
          const maxUploadedFileSize = maxUploadedFileSizeValidator(question.options.maxFileSizeInBytes);
          const allowedFileMimeTypes = mimeTypeUploadedFileValidator(question.options.allowedMimeTypes, question.options.allowedExtensions);
          validationRules = {
            ...validationRules, maxUploadedFileSize, allowedFileMimeTypes,
          };
        }

        question.validationRules = validationRules;

        return question;
      })
      .reduce((carry, question) => {
        carry[question.id] = question.validationRules;
        delete question.validationRules;

        return carry;
      }, {});

    // console.log(formStructure);

    return { form: formStructure };
  },
};
</script>

<style scoped lang="scss">
.RMPE-CustomForm {

}

.RMPE-CustomOptIn {
  padding: 30px;
  margin-top: 20px;
  margin-bottom: 20px;
  color: #FFF;
  position: relative;
  //background-color: var(--form-font-color);
}

.RMPE-CustomOptIn-CloseIcon {
  position: absolute;
  top: 20px;
  right: 20px;
  cursor: pointer;
  font-size: 24px;
}
.RMPE-CustomOptIn-Content {
  white-space: pre-wrap;
  background: transparent;
  padding: 0;
  color: #FFF;
  font-family: inherit;
}
</style>
